import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Plus, MessageSquare, Trash2, LogOut, Bot, Receipt, Palmtree, FileText, Shield, ClipboardList, Stethoscope, Fingerprint, Wallet } from 'lucide-react';
import HCLogo from './HCLogo';
import LanguageSelector from './LanguageSelector';
import { CATEGORIES } from '../mock';
import { formatRelativeTime } from '../lib/formatters';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from './ui/alert-dialog';

const ICON_MAP = { Receipt, Palmtree, FileText, Shield };

const Sidebar = ({ chats, activeChatId, onNewChat, onSelectChat, onDeleteChat, isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const goToRequests = (tab) => {
    navigate('/requests' + (tab ? `?tab=${tab}` : ''));
    onClose?.();
  };

  const goToChat = () => {
    navigate('/');
    onClose?.();
  };

  const isOnChat = location.pathname === '/' || location.pathname.startsWith('/chat');
  const isOnRequests = location.pathname === '/requests';

  return (
    <aside
      className={`
        fixed lg:relative inset-y-0 left-0 z-40
        w-[280px] lg:w-[280px] flex-shrink-0
        bg-slate-50 border-r border-slate-200
        flex flex-col
        transform transition-transform duration-200
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
      `}
    >
      {/* Brand header */}
      <div className="h-14 px-4 flex items-center justify-between border-b border-slate-200">
        <div className="flex items-center gap-2.5">
          <HCLogo size="md" />
          <div>
            <div className="text-sm font-semibold text-slate-900 leading-tight">{t('sidebar.brand')}</div>
            <div className="text-[10px] text-slate-500 leading-tight">{t('sidebar.platform')}</div>
          </div>
        </div>
      </div>

      {/* New chat button */}
      <div className="p-3">
        <Button
          onClick={() => { onNewChat(); goToChat(); }}
          className="w-full h-10 bg-blue-700 hover:bg-blue-800 text-white font-medium gap-2 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          {t('sidebar.newChat')}
        </Button>
      </div>

      {/* HR Services quick actions */}
      <div className="px-3 pb-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 px-2 pb-2">
          {t('sidebar.hrServices')}
        </div>
        <div className="space-y-0.5">
          <button
            onClick={() => { navigate('/attendance'); onClose?.(); }}
            className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors ${
              location.pathname === '/attendance' ? 'bg-blue-100 text-blue-900' : 'text-slate-700 hover:bg-white hover:text-blue-800'
            }`}
          >
            <div className="w-6 h-6 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
              <Fingerprint className="w-3.5 h-3.5" />
            </div>
            <span className="truncate">{t('sidebar.attendance')}</span>
          </button>
          <button
            onClick={() => goToRequests('vacation')}
            className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors ${
              isOnRequests ? 'bg-blue-100 text-blue-900' : 'text-slate-700 hover:bg-white hover:text-blue-800'
            }`}
          >
            <div className="w-6 h-6 rounded-md bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-3.5 h-3.5" />
            </div>
            <span className="truncate">{t('sidebar.vacation')}</span>
          </button>
          <button
            onClick={() => goToRequests('sickness')}
            className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors ${
              isOnRequests ? 'bg-blue-100 text-blue-900' : 'text-slate-700 hover:bg-white hover:text-blue-800'
            }`}
          >
            <div className="w-6 h-6 rounded-md bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-3.5 h-3.5" />
            </div>
            <span className="truncate">{t('sidebar.sickness')}</span>
          </button>
          <button
            onClick={() => { navigate('/payslips'); onClose?.(); }}
            className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors ${
              location.pathname === '/payslips' ? 'bg-blue-100 text-blue-900' : 'text-slate-700 hover:bg-white hover:text-blue-800'
            }`}
          >
            <div className="w-6 h-6 rounded-md bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
              <Wallet className="w-3.5 h-3.5" />
            </div>
            <span className="truncate">{t('sidebar.payslips')}</span>
          </button>
        </div>
      </div>

      {/* Categories quick access */}
      <div className="px-3 pb-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 px-2 pb-2">
          {t('sidebar.chatCategories')}
        </div>
        <div className="space-y-0.5">
          {CATEGORIES.map(cat => {
            const Icon = ICON_MAP[cat.icon] || MessageSquare;
            return (
              <button
                key={cat.id}
                onClick={() => { onNewChat(); goToChat(); }}
                className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm text-slate-700 hover:bg-white hover:text-blue-800 transition-colors"
              >
                <div className="w-6 h-6 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className="truncate">{cat.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chat history */}
      <div className="flex-1 overflow-y-auto custom-scroll px-3 pt-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 px-2 pb-2">
          {t('sidebar.history')}
        </div>
        {chats.length === 0 && (
          <div className="px-2 py-4 text-xs text-slate-400">
            {t('sidebar.noChats')}
          </div>
        )}
        <div className="space-y-0.5">
          {chats.map(chat => (
            <div
              key={chat.id}
              className={`group flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${
                activeChatId === chat.id
                  ? 'bg-blue-100 text-blue-900'
                  : 'text-slate-700 hover:bg-white'
              }`}
              onClick={() => { onSelectChat(chat.id); }}
            >
              <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{chat.title}</div>
                <div className="text-[10px] text-slate-400 truncate">{formatRelativeTime(chat.updatedAt)}</div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 hover:text-red-600 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('sidebar.deleteChatTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('sidebar.deleteChatBody', { title: chat.title })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => onDeleteChat(chat.id)}
                    >
                      {t('common.delete')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      </div>

      {/* User footer */}
      <div className="border-t border-slate-200 p-3 space-y-2">
        <LanguageSelector variant="default" size="sm" className="w-full" />
        <div className="flex items-center gap-2.5 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-xs font-semibold">
            {user?.initials || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            className="p-1.5 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
            title={t('sidebar.logoutTooltip')}
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
